import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { cleanHtml } from '../utils/sanitize';
import { useAuth } from 'react-oidc-context';
import { getAntennesStats, getContacts, setAuthToken, getSetting, updateSetting, getUploadsUrl, getGatewayUploadsUrl } from '../services/api';
import { usePerm } from '../lib/perms';
import { Pencil, Save, X as XIcon, Bold, Italic, Underline, List } from 'lucide-react';
import { toast } from 'react-toastify';
import './Dashboard.css';

const DASHBOARD_TEXT_KEY = 'dashboard.free_text';
const DEFAULT_TEXT_HTML = '<p style="margin:0;color:#64748b">Cliquez sur le crayon pour personnaliser ce bloc.</p>';

function Dashboard() {
  const auth = useAuth();
  const [stats, setStats] = useState(null);
  const [recentContacts, setRecentContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Free text cartouche
  const [textHtml, setTextHtml] = useState(DEFAULT_TEXT_HTML);
  const [editing, setEditing] = useState(false);
  const [savingText, setSavingText] = useState(false);
  const editorRef = useRef(null);
  const canEditText = usePerm('dashboard.edit_text');

  useEffect(() => {
    if (auth.user?.access_token) {
      setAuthToken(auth.user.access_token);
      loadData();
      loadText();
    }
  }, [auth.user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsData, recentData] = await Promise.all([
        getAntennesStats(),
        getContacts({ limit: 6, sort: 'created', order: 'DESC' }),
      ]);
      setStats(statsData);
      setRecentContacts(recentData.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadText = async () => {
    try {
      const value = await getSetting(DASHBOARD_TEXT_KEY);
      if (value && typeof value === 'string') setTextHtml(value);
    } catch (e) { /* not set yet — keep default */ }
  };

  const startEdit = () => {
    setEditing(true);
    // Defer setting innerHTML until the editor is mounted
    setTimeout(() => {
      if (editorRef.current) editorRef.current.innerHTML = textHtml;
    }, 0);
  };

  const cancelEdit = () => { setEditing(false); };

  const saveText = async () => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    setSavingText(true);
    try {
      await updateSetting(DASHBOARD_TEXT_KEY, html);
      setTextHtml(html);
      setEditing(false);
      toast.success('Texte enregistré');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSavingText(false);
    }
  };

  const exec = (cmd, value = null) => {
    document.execCommand(cmd, false, value);
    editorRef.current?.focus();
  };

  const getStatusPillClass = (etat) => {
    if (!etat) return 'status-pill-actif';
    const e = etat.toLowerCase();
    if (e.includes('actif') && !e.includes('inactif')) return 'status-pill-actif';
    if (e.includes('pause')) return 'status-pill-pause';
    if (e.includes('inactif')) return 'status-pill-inactif';
    return 'status-pill-actif';
  };

  const QUALIFICATION_BG = {
    'antenne_principale': '#bf2dfb',
    'profil_intéressant': '#6687d2',
    'historique_antenne': '#4d4d4d',
    'antenne_binome': '#f98090',
    'nouveau': '#db2777',
  };
  const QUALIFICATION_TEXT = {
    'antenne_principale': '#ffffff',
    'profil_intéressant': '#ffffff',
    'historique_antenne': '#ffffff',
    'antenne_binome': '#ffffff',
    'nouveau': '#ffffff',
  };
  const QUALIFICATION_LABEL = {
    'antenne_principale': 'Antenne actuelle',
    'historique_antenne': 'Ancienne antenne',
    'profil_intéressant': 'Profil intéressant',
    'antenne_binome': 'Binôme',
    'nouveau': 'Nouveau',
  };

  const formatDateAjout = (d) => {
    if (!d) return '--';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '--';
    return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const initials = (contact) => {
    const s = (contact.raison_sociale || `${contact.prenom || ''} ${contact.nom || ''}`).trim();
    return s.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || '?';
  };

  return (
    <div className="dashboard">
      <header className="page-header">
        <div>
          <h1>Tableau de bord</h1>
          <p className="page-subtitle">Antennes Selfizee — Vue d'ensemble du réseau</p>
        </div>
      </header>

      {/* KPI Cards — order: contacts actifs, villes (≥1 contact actif), classik, spherik, prestige */}
      <div className="kpi-grid">
        <Link to="/contacts?etat=actif&qualifications=antenne_principale" className="kpi-card kpi-antennes kpi-card-link" title="Voir les contacts antennes actifs">
          <div className="kpi-card-inner">
            <div className="kpi-card-header">
              <span className="kpi-card-title">Contacts antennes actifs</span>
              <span className="kpi-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                </svg>
              </span>
            </div>
            <div className="kpi-card-value">{loading ? '...' : (stats?.total_contacts_actifs || 0)}</div>
          </div>
        </Link>

        <Link to="/contacts?borne_types=classik" className="kpi-card kpi-classik kpi-card-link" title="Voir les antennes équipées Classik">
          <div className="kpi-card-inner">
            <div className="kpi-card-header">
              <span className="kpi-card-title">Antennes Classik</span>
              <span className="kpi-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="4" y="4" width="16" height="16" rx="2" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </span>
            </div>
            <div className="kpi-card-value">{loading ? '...' : (stats?.total_classik || 0)}</div>
          </div>
        </Link>

        <Link to="/contacts?borne_types=spherik" className="kpi-card kpi-spherik kpi-card-link" title="Voir les antennes équipées Spherik">
          <div className="kpi-card-inner">
            <div className="kpi-card-header">
              <span className="kpi-card-title">Antennes Spherik</span>
              <span className="kpi-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <ellipse cx="12" cy="12" rx="4" ry="10" />
                  <path d="M2 12h20" />
                </svg>
              </span>
            </div>
            <div className="kpi-card-value">{loading ? '...' : (stats?.total_spherik || 0)}</div>
          </div>
        </Link>

        <Link to="/contacts?borne_types=prestige" className="kpi-card kpi-prestige kpi-card-link" title="Voir les antennes équipées Prestige">
          <div className="kpi-card-inner">
            <div className="kpi-card-header">
              <span className="kpi-card-title">Antennes Prestige</span>
              <span className="kpi-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              </span>
            </div>
            <div className="kpi-card-value">{loading ? '...' : (stats?.total_prestige || 0)}</div>
          </div>
        </Link>

        <Link to="/antennes" className="kpi-card kpi-villes kpi-card-link" title="Voir les villes principales">
          <div className="kpi-card-inner">
            <div className="kpi-card-header">
              <span className="kpi-card-title">Villes principales</span>
              <span className="kpi-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </span>
            </div>
            <div className="kpi-card-value">{loading ? '...' : (stats?.total_villes_avec_contact_actif || 0)}</div>
          </div>
        </Link>
      </div>

      {/* Free text cartouche (replaces map + par-région) */}
      <section
        className="dashboard-freetext-card"
        style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          marginBottom: 16,
          overflow: 'hidden',
        }}
        onMouseEnter={(e) => e.currentTarget.classList.add('hovered')}
        onMouseLeave={(e) => e.currentTarget.classList.remove('hovered')}
      >
        {!editing ? (
          <div style={{ position: 'relative', padding: '20px 24px', minHeight: 120 }}>
            {canEditText && (
              <button
                onClick={startEdit}
                data-perm="antennes:dashboard.edit_text"
                title="Modifier le texte"
                className="dashboard-freetext-edit-btn"
              >
                <Pencil size={14} />
              </button>
            )}
            <div
              dangerouslySetInnerHTML={{ __html: cleanHtml(textHtml) }}
              style={{ fontSize: 14, lineHeight: 1.6, color: '#0f172a' }}
            />
          </div>
        ) : (
          <div style={{ padding: 16 }}>
            <div style={{
              display: 'flex', gap: 4, marginBottom: 8, padding: 6,
              background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0',
            }}>
              <button onClick={() => exec('bold')} title="Gras" style={toolbarBtnStyle}><Bold size={14} /></button>
              <button onClick={() => exec('italic')} title="Italique" style={toolbarBtnStyle}><Italic size={14} /></button>
              <button onClick={() => exec('underline')} title="Souligné" style={toolbarBtnStyle}><Underline size={14} /></button>
              <span style={{ width: 1, background: '#e2e8f0', margin: '4px 4px' }} />
              <button onClick={() => exec('insertUnorderedList')} title="Liste à puces" style={toolbarBtnStyle}><List size={14} /></button>
              <button onClick={() => exec('formatBlock', 'h3')} title="Titre" style={{ ...toolbarBtnStyle, fontSize: 11, fontWeight: 700 }}>H</button>
              <button onClick={() => exec('formatBlock', 'p')} title="Paragraphe" style={{ ...toolbarBtnStyle, fontSize: 11 }}>P</button>
            </div>
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              style={{
                minHeight: 120, padding: '12px 14px',
                border: '1px solid #e2e8f0', borderRadius: 8,
                fontSize: 14, lineHeight: 1.6, color: '#0f172a',
                outline: 'none', background: 'white',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button
                onClick={cancelEdit}
                disabled={savingText}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 8,
                  border: '1px solid #e2e8f0', background: 'white', color: '#475569',
                  fontSize: 13, cursor: 'pointer',
                }}
              >
                <XIcon size={14} /> Annuler
              </button>
              <button
                onClick={saveText}
                disabled={savingText}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 8,
                  border: 'none', background: '#4f46e5', color: 'white',
                  fontSize: 13, cursor: 'pointer', opacity: savingText ? 0.6 : 1,
                }}
              >
                <Save size={14} /> {savingText ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Contacts récents */}
      <section className="dashboard-contacts-card">
        <div className="card-header-gradient card-header-gradient-contacts">
          <div className="card-header-left">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
            </svg>
            <h2>Contacts récents</h2>
          </div>
          <a href="/contacts" className="card-header-link">Tous les contacts</a>
        </div>
        <div className="contacts-table-wrapper">
          <table className="k-table">
            <thead>
              <tr>
                <th>Contact</th>
                <th>Entreprise</th>
                <th>Date d'ajout</th>
                <th>Ajouté par</th>
                <th>Ville principale</th>
                <th>Type</th>
                <th>Matériels</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="loading-cell"><div className="spinner-sm"></div> Chargement...</td></tr>
              ) : recentContacts.length === 0 ? (
                <tr><td colSpan="8" className="empty-cell">Aucun contact</td></tr>
              ) : (
                recentContacts.map((contact) => {
                  const qLabel = QUALIFICATION_LABEL[contact.qualification];
                  const qBg = QUALIFICATION_BG[contact.qualification];
                  const qText = QUALIFICATION_TEXT[contact.qualification];
                  const nbBornes = Array.isArray(contact.bornes_list) ? contact.bornes_list.length : 0;
                  const nbAccessoires = Array.isArray(contact.accessoires_list)
                    ? contact.accessoires_list.reduce((sum, a) => sum + (parseInt(a.count, 10) || 0), 0)
                    : 0;
                  const totalMateriels = nbBornes + nbAccessoires;
                  return (
                    <tr key={contact.id} onClick={() => window.location.href = `/contacts/${contact.id}`}>
                      <td>
                        <div className="contact-name-cell" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                          <div className="contact-avatar">
                            {contact.photo ? (
                              <img src={getUploadsUrl(contact.photo)} alt="" />
                            ) : (
                              <span>{initials(contact)}</span>
                            )}
                          </div>
                          <span className="contact-name">{`${contact.prenom || ''} ${contact.nom || ''}`.trim() || '--'}</span>
                        </div>
                      </td>
                      <td>
                        <span className="contact-name" style={{ fontSize: 13 }}>
                          {contact.raison_sociale || contact.enseigne || contact.etablissement || '--'}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--k-muted)', whiteSpace: 'nowrap' }}>
                        {formatDateAjout(contact.created)}
                      </td>
                      <td>
                        {contact.creator_prenom || contact.creator_nom ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                                title={`${contact.creator_prenom || ''} ${contact.creator_nom || ''}`.trim()}>
                            <span className="contact-avatar" style={{ width: 26, height: 26, fontSize: 10 }}>
                              {contact.creator_photo
                                ? <img src={getGatewayUploadsUrl(contact.creator_photo)} alt="" />
                                : <span>{`${(contact.creator_prenom || '')[0] || ''}${(contact.creator_nom || '')[0] || ''}`.toUpperCase() || '?'}</span>}
                            </span>
                            <span style={{ fontSize: 12 }}>{`${contact.creator_prenom || ''} ${contact.creator_nom || ''}`.trim()}</span>
                          </span>
                        ) : (
                          <span style={{ fontSize: 12, color: 'var(--k-muted)' }}>—</span>
                        )}
                      </td>
                      <td>
                        <span className="contact-location" style={{ fontSize: 12 }}>
                          {contact.antenne_ville || contact.ville || '--'}
                        </span>
                      </td>
                      <td>
                        {qLabel ? (
                          <span
                            className="contact-type-badge"
                            style={{ background: qBg, color: qText, fontWeight: 600 }}
                          >
                            {qLabel}
                          </span>
                        ) : (
                          <span className="contact-type-badge contact-type-default">
                            {contact.qualification || '--'}
                          </span>
                        )}
                      </td>
                      <td style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                        {totalMateriels}
                      </td>
                      <td>
                        <span className={`status-pill ${getStatusPillClass(contact.etat)}`}>
                          <span className="status-pill-dot"></span>
                          {contact.etat || 'actif'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

const toolbarBtnStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, borderRadius: 6,
  border: 'none', background: 'transparent', color: '#475569', cursor: 'pointer',
};

export default Dashboard;
